import React from "react";
import { connect } from "react-redux";

import Form from "antd/es/form";
import Radio from "antd/es/radio";
import { useIntl } from "react-intl";

import User from "@mapmycustomers/shared/types/User";
import { NumberField } from "@mapmycustomers/ui";

import CurrencyInput from "@app/component/input/CurrencyInput";
import RadioGroup from "@app/component/input/RadioGroup";
import GoalType, { getGoalTypeDisplayName } from "@app/enum/dashboard/GoalType";
import GoalDetailsProps from "@app/scene/dashboard/types/GoalDetailsProps";
import useCard from "@app/scene/dashboard/utils/useCard";
import { isMember } from "@app/store/iam/util";
import { getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";

import AmountDetails from "./AmountDetails";
import getTargetFieldLabel from "./getTargetFieldLabel";
import GoalAlert from "./GoalAlert";
import styles from "./GoalDetails.module.scss";
import GoalTargetType from "./GoalTargetType";

interface Props extends GoalDetailsProps {
  users: User[];
}

const GoalDetails: React.FC<Props> = ({
  allowGoalTypeChange,
  currencyType,
  currentUser,
  goalConfiguration,
  goalConfigurationProps,
  scope,
  teamGoal,
  type,
  users,
}) => {
  const intl = useIntl();
  const { getGoalDescription, getGoalType } = useCard(type);
  const { editable, individualGoal, value, visible } = goalConfigurationProps;

  const enabled = Form.useWatch(["goal", "enabled"]);
  const { format, goalType: defaultGoalType } = getGoalType({ configuration: goalConfiguration });
  const goalType: GoalType = Form.useWatch(["goal", "goalType"]) ?? defaultGoalType;

  if (!enabled && !visible) {
    return null;
  }

  return (
    <div className={styles.container}>
      {allowGoalTypeChange && (
        <Form.Item name={["goal", "goalType"]}>
          <RadioGroup defaultValue={GoalType.MIN_GOAL}>
            <Radio.Button value={GoalType.MIN_GOAL}>
              {getGoalTypeDisplayName(intl, GoalType.MIN_GOAL)}
            </Radio.Button>
            <Radio.Button value={GoalType.MAX_GOAL}>
              {getGoalTypeDisplayName(intl, GoalType.MAX_GOAL)}
            </Radio.Button>
          </RadioGroup>
        </Form.Item>
      )}
      <div className={styles.description}>
        {getGoalDescription(intl, { goalType, scope }, users)}
      </div>
      <GoalAlert
        goalFormat={format}
        goalType={goalType}
        isMember={isMember(currentUser)}
        scope={scope}
        teamGoal={teamGoal}
      />
      {editable ? (
        individualGoal ? (
          <Form.Item label={getTargetFieldLabel(intl, goalType)} name={["individualGoal"]}>
            {currencyType ? (
              <CurrencyInput max={teamGoal?.target} min={0} />
            ) : (
              <NumberField
                formatter={
                  format === "time"
                    ? (value) =>
                        intl.formatNumber(Number(value), {
                          style: "unit",
                          unit: "hour",
                          unitDisplay: "narrow",
                        })
                    : undefined
                }
                fullWidth
                max={teamGoal?.target}
                min={0}
                parser={format === "time" ? (value) => value?.replace(/\D/g, "") ?? 0 : undefined}
                step={1}
              />
            )}
          </Form.Item>
        ) : (
          <Form.Item label={getTargetFieldLabel(intl, goalType)} name={["goal", "target"]}>
            {currencyType ? (
              <CurrencyInput min={0} />
            ) : (
              <NumberField
                formatter={
                  format === "time"
                    ? (value) =>
                        intl.formatNumber(Number(value), {
                          style: "unit",
                          unit: "hour",
                          unitDisplay: "narrow",
                        })
                    : undefined
                }
                fullWidth
                min={0}
                parser={format === "time" ? (value) => value?.replace(/\D/g, "") ?? 0 : undefined}
                step={1}
              />
            )}
          </Form.Item>
        )
      ) : (
        <AmountDetails goalType={goalType} value={value} />
      )}
      {scope.teamId && !scope.userId ? (
        <GoalTargetType
          currencyType={currencyType}
          goalConfiguration={goalConfiguration}
          teamId={scope.teamId}
        />
      ) : null}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  users: getUsers(state),
});

export default connect(mapStateToProps)(GoalDetails);
